/**
 * How many calls a provider takes at once, learned (docs/translations/02-submit.md).
 *
 * ElevenLabs' dubbing, Scribe and AudioShake each cap concurrent work per
 * account, and the cap is not published (it moves with the plan). Past it a
 * provider refuses (`concurrent_limit_exceeded`, 429) or - ElevenLabs dubbing -
 * accepts the project and silently drops its language. So the number is
 * found by trying:
 *
 * - Start at `LIMITER.start` (3).
 * - After a streak of `LIMITER.streak` calls started without a refusal, allow one
 *   more (`probing`). The first call that really runs at that height and starts
 *   cleanly makes it the new `level`.
 * - A refusal drops back to the last level that worked (the call is not failed:
 *   the caller waits for a slot and tries again), and nothing is probed for
 *   `LIMITER.cooldownMs`.
 * - Never above `ceiling` (the worker's env, default 20), never below 1.
 *
 * The state is the platform's (one row per provider): every worker and every
 * restart shares what was learned. These functions are the whole policy; the
 * API applies them under a row lock, and the tests drive them against a fake
 * provider that refuses above N.
 */

export const LIMITER = {
  start: 3,
  /** Clean starts in a row before one more is allowed. */
  streak: 3,
  /** After a refusal, how long before probing upward again. */
  cooldownMs: 10 * 60_000,
  ceiling: 20,
} as const;

/** The providers the worker gates, and what one of their calls is called in the UI. */
export const LIMITED_PROVIDERS = ["elevenlabs", "scribe", "audioshake"] as const;
export type LimitedProvider = (typeof LIMITED_PROVIDERS)[number];

export interface LimiterState {
  /** The highest number of concurrent calls seen to start cleanly. */
  level: number;
  /** One above `level` while it is being tried; null otherwise. */
  probing: number | null;
  /** Clean starts since the last change. */
  streak: number;
  /** No probing before this (epoch ms); null when free to. */
  cooldownUntil: number | null;
  ceiling: number;
}

export function initialLimiter(ceiling: number = LIMITER.ceiling): LimiterState {
  const c = clampCeiling(ceiling);
  return { level: Math.min(LIMITER.start, c), probing: null, streak: 0, cooldownUntil: null, ceiling: c };
}

export function clampCeiling(ceiling: number): number {
  return Math.max(1, Math.min(100, Math.floor(Number.isFinite(ceiling) ? ceiling : LIMITER.ceiling)));
}

/** How many may run now. */
export function limiterAllowed(state: LimiterState): number {
  return Math.min(state.ceiling, state.probing ?? state.level);
}

/**
 * A caller asks for a slot while `inUse` are held. Answers whether it gets
 * one, the state after (a probe may open), and the height it runs at - the
 * number it reports back with `limiterStarted` or `limiterRefused`.
 */
export function limiterGrant(state: LimiterState, inUse: number, now: number): { granted: boolean; at: number; state: LimiterState } {
  let next = { ...state, level: Math.min(state.level, state.ceiling) };
  if (
    next.probing === null &&
    next.streak >= LIMITER.streak &&
    next.level < next.ceiling &&
    (next.cooldownUntil === null || now >= next.cooldownUntil)
  ) {
    next = { ...next, probing: next.level + 1, streak: 0, cooldownUntil: null };
  }
  const granted = inUse < limiterAllowed(next);
  return { granted, at: inUse + 1, state: next };
}

/** A call granted at height `at` started without a refusal. */
export function limiterStarted(state: LimiterState, at: number): LimiterState {
  let next = { ...state, streak: state.streak + 1 };
  if (at > next.level) {
    // It really ran at this height: that is the new level.
    next = { ...next, level: Math.min(at, next.ceiling), streak: 0 };
    if (next.probing !== null && next.probing <= next.level) next.probing = null;
  }
  return next;
}

/**
 * A call granted at height `at` was refused for concurrency: back to the last
 * height that worked, and a cool-down before trying higher.
 */
export function limiterRefused(state: LimiterState, at: number, now: number): LimiterState {
  const level = Math.max(1, Math.min(state.level, at - 1));
  return { ...state, level, probing: null, streak: 0, cooldownUntil: now + LIMITER.cooldownMs };
}

/** "ElevenLabs", "Scribe", "AudioShake". */
export function providerLabel(provider: string): string {
  if (/scribe/i.test(provider)) return "ElevenLabs Scribe";
  if (/eleven/i.test(provider)) return "ElevenLabs";
  if (/audioshake/i.test(provider)) return "AudioShake";
  return provider;
}

/** Which account a call is billed to: Scribe and dubbing are both ElevenLabs credits. */
export function billingProvider(provider: string | null | undefined): "elevenlabs" | "audioshake" | null {
  if (!provider) return null;
  if (/eleven|scribe|dub|transcribe/i.test(provider)) return "elevenlabs";
  if (/audioshake|separate|stems/i.test(provider)) return "audioshake";
  return null;
}

/** Where an admin tops the account up. */
export const TOP_UP_URL: Record<"elevenlabs" | "audioshake", string> = {
  elevenlabs: "https://elevenlabs.io/app/subscription",
  audioshake: "https://www.audioshake.ai/",
};
